import type {MantineTheme} from '@mantine/core'
import {NavLink, Tooltip} from '@mantine/core'
import {Link, useMatchRoute} from '@tanstack/react-router'
import {ReactNode} from 'react'

type SidebarNavLinkProps = {
    narrow?: boolean
    label: string
    to?: string
    icon: ReactNode
    exact?: boolean
    active?: boolean
    variant?: 'light' | 'subtle'
    onClick?: () => void
}

export default function SidebarNavLink({
    narrow = false,
    label,
    to,
    icon,
    exact,
    active,
    variant = 'light',
    onClick,
}: SidebarNavLinkProps) {
    const matchRoute = useMatchRoute()
    const color = variant === 'light' ? 'green' : 'gray'

    const routeMatches = Boolean(
        to &&
        matchRoute({
            to,
            fuzzy: !exact,
        }),
    )
    const navActive = active ?? routeMatches

    const narrowStyles = narrow
        ? {
              root: {justifyContent: 'center'},
              label: {display: 'none'},
              section: {margin: 0},
          }
        : undefined

    const styles =
        variant === 'light' && color === 'green'
            ? (t: MantineTheme) => ({
                  ...narrowStyles,
                  root: {
                      ...narrowStyles?.root,
                      ...(navActive
                          ? {
                                backgroundColor: t.colors.green[1],
                                borderLeft: `3px solid ${t.colors.green[9]}`,
                            }
                          : {
                                borderLeft: '3px solid transparent',
                            }),
                  },
                  label: {
                      ...narrowStyles?.label,
                      ...(navActive ? {color: t.colors.green[9], fontWeight: 600} : {}),
                  },
                  section: {
                      ...narrowStyles?.section,
                      ...(navActive ? {color: t.colors.green[9]} : {}),
                  },
              })
            : narrowStyles

    // TanStack `Link` defaults `to` to the current location when omitted, so it would always look
    // active. Use a plain button for in-place actions (no navigation).
    const navLink = to ? (
        <NavLink
            component={Link}
            to={to}
            onClick={onClick}
            label={label}
            leftSection={icon}
            color={color}
            variant={variant}
            activeOptions={exact ? {exact: true} : undefined}
            active={navActive}
            aria-label={narrow ? label : undefined}
            styles={styles}
        />
    ) : (
        <NavLink
            component="button"
            type="button"
            onClick={onClick}
            label={label}
            leftSection={icon}
            color={color}
            variant={variant}
            active={navActive}
            aria-label={narrow ? label : undefined}
            styles={styles}
        />
    )

    return narrow ? (
        <Tooltip label={label} position="right" openDelay={400}>
            {navLink}
        </Tooltip>
    ) : (
        navLink
    )
}
